// Miky Motorsport - okolni scenerie (D): biom-aware. Stromy/kaktusy/budovy + tribuny +
// billboardy + pneu bariery + vzdalene kopce. Vse vizualni (zadna kolize). Instancing.
import * as THREE from 'three';

function mulberry32(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// track: { pts, pointAtS, normalAt, totalLength }; biome: viz data/tracks.js
export function buildScenery(scene, track, biome = {}) {
  const objects = [];
  const rnd = mulberry32(20260606);
  const pts = track.pts;
  const N = pts.length;
  const type = biome.scenery || 'forest';
  const night = type === 'city';
  const gy = track.groundY || (() => 0); // vyska terenu (kopce) pro usazeni objektu

  function distToTrack(x, z) {
    let best = Infinity;
    for (let i = 0; i < N; i += 3) {
      const dx = pts[i].x - x, dz = pts[i].z - z;
      const d = dx * dx + dz * dz;
      if (d < best) best = d;
    }
    return Math.sqrt(best);
  }
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of pts) { minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x); minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z); }

  // --- Okolni "porost" (instanced): les=stromy, pousti=kaktusy, mesto=budovy ---
  const disposables = [];
  if (type === 'city') {
    buildBuildings();
  } else {
    buildFoliage();
  }

  function place(count, guardMul, minD, maxD, setFn) {
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), pos = new THREE.Vector3(), scl = new THREE.Vector3();
    let placed = 0, guard = 0;
    while (placed < count && guard < count * guardMul) {
      guard++;
      const x = minX - 40 + rnd() * (maxX - minX + 80);
      const z = minZ - 40 + rnd() * (maxZ - minZ + 80);
      const d = distToTrack(x, z);
      if (d < minD || d > maxD) continue;
      setFn(placed, x, z, m, q, pos, scl);
      placed++;
    }
    return placed;
  }

  function buildFoliage() {
    const desert = type === 'desert';
    const TREE_N = desert ? 70 : 170;
    const treeCol = new THREE.Color(biome.tree || 0x2f6b2f);
    const trunkGeo = desert
      ? new THREE.CylinderGeometry(0.14, 0.18, 1.4, 6)
      : new THREE.CylinderGeometry(0.22, 0.34, 2.0, 5);
    trunkGeo.translate(0, desert ? 0.7 : 1.0, 0);
    // poust = kaktus (uzky valec), jinak jehlican (kuzel)
    const foliageGeo = desert
      ? new THREE.CylinderGeometry(0.22, 0.26, 2.2, 7)
      : new THREE.ConeGeometry(1.9, 4.6, 7);
    foliageGeo.translate(0, desert ? 1.5 : 4.0, 0);
    const trunkMat = new THREE.MeshStandardMaterial({ color: desert ? 0x9a7a48 : 0x5a3a22, roughness: 0.95 });
    const foliageMat = new THREE.MeshStandardMaterial({ color: treeCol, roughness: 0.85, flatShading: true });
    const trunks = new THREE.InstancedMesh(trunkGeo, trunkMat, TREE_N);
    const foliage = new THREE.InstancedMesh(foliageGeo, foliageMat, TREE_N);
    trunks.castShadow = true; foliage.castShadow = true;
    const col = new THREE.Color();
    const placed = place(TREE_N, 14, 19, 95, (i, x, z, m, q, pos, scl) => {
      const s = desert ? (0.6 + rnd() * 0.5) : (0.7 + rnd() * 0.9);
      pos.set(x, gy(x, z), z);
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rnd() * Math.PI * 2);
      scl.set(s, s * (0.9 + rnd() * 0.4), s);
      m.compose(pos, q, scl);
      trunks.setMatrixAt(i, m); foliage.setMatrixAt(i, m);
      const j = 0.85 + rnd() * 0.3;
      col.copy(treeCol).multiplyScalar(j);
      foliage.setColorAt(i, col);
    });
    trunks.count = placed; foliage.count = placed;
    trunks.instanceMatrix.needsUpdate = true; foliage.instanceMatrix.needsUpdate = true;
    if (foliage.instanceColor) foliage.instanceColor.needsUpdate = true;
    scene.add(trunks); scene.add(foliage); objects.push(trunks, foliage);
    disposables.push(trunkGeo, foliageGeo, trunkMat, foliageMat);
  }

  function buildBuildings() {
    const B_N = 120;
    const geo = new THREE.BoxGeometry(1, 1, 1); geo.translate(0, 0.5, 0);
    const mat = new THREE.MeshStandardMaterial({ color: 0x10131c, roughness: 0.8, metalness: 0.2,
      emissive: 0x223066, emissiveIntensity: 0.5 });
    const mesh = new THREE.InstancedMesh(geo, mat, B_N);
    mesh.castShadow = true;
    const col = new THREE.Color();
    const neon = [0x33e2ff, 0xff3aa0, 0x8a5cff, 0x2ee6a0, 0xffd23a];
    const placed = place(B_N, 16, 22, 130, (i, x, z, m, q, pos, scl) => {
      const w = 6 + rnd() * 12, h = 10 + rnd() * 46, d = 6 + rnd() * 12;
      pos.set(x, gy(x, z), z);
      q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.round(rnd() * 4) * Math.PI / 2);
      scl.set(w, h, d);
      m.compose(pos, q, scl);
      mesh.setMatrixAt(i, m);
      // par budov "sviti" neonem
      if (rnd() < 0.35) col.set(neon[(i * 7) % neon.length]).multiplyScalar(0.5);
      else col.set(0x11151f);
      mesh.setColorAt(i, col);
    });
    mesh.count = placed;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    scene.add(mesh); objects.push(mesh);
    disposables.push(geo, mat);
  }

  // helper: pozice + uhel u trati ve vzdalenosti `off` na strane `side`
  const L = track.totalLength;
  function atTrack(s, side, off) {
    const p = track.pointAtS(((s % L) + L) % L);
    const i = Math.round((((s % L) + L) % L) / L * N) % N;
    const n = track.normalAt(i);
    return { x: p.x + side * n.x * off, z: p.z + side * n.z * off, yaw: Math.atan2(n.x, n.z) };
  }

  // --- Tribuny podel hlavni rovinky (u startu) ---
  const standMat = new THREE.MeshStandardMaterial({ color: 0x40454f, roughness: 0.85 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0xd23b30, roughness: 0.6, metalness: 0.1 });
  const crowdTex = makeCrowdTexture();
  const crowdMat = new THREE.MeshStandardMaterial({ map: crowdTex, roughness: 0.9 });
  for (let k = 0; k < 3; k++) {
    const at = atTrack(18 + k * 26, 1, 18);
    const stand = new THREE.Group();
    const base = new THREE.Mesh(new THREE.BoxGeometry(24, 7, 11), standMat);
    base.position.y = 3.5; base.castShadow = true; base.receiveShadow = true; stand.add(base);
    const crowd = new THREE.Mesh(new THREE.PlaneGeometry(23, 8.5), crowdMat);
    crowd.position.set(0, 4.2, -5.7); crowd.rotation.x = -Math.PI * 0.18; stand.add(crowd);
    const roof = new THREE.Mesh(new THREE.BoxGeometry(25, 0.5, 7), roofMat);
    roof.position.set(0, 7.6, 1.4); roof.castShadow = true; stand.add(roof);
    stand.position.set(at.x, gy(at.x, at.z), at.z);
    stand.rotation.y = at.yaw + Math.PI;
    scene.add(stand); objects.push(stand);
  }

  // --- Billboardy (sponzori / neony) podel trati ---
  const billCols = night ? [0x33e2ff, 0xff3aa0, 0x8a5cff, 0x2ee6a0, 0xffd23a, 0xff5e3a]
    : [0xff5e3a, 0x2e6cff, 0x2ecc71, 0xf1c40f, 0xe74c3c, 0x1abc9c];
  const billN = night ? 11 : 7;
  for (let k = 0; k < billN; k++) {
    const s = (k / billN) * L + 30;
    const side = k % 2 === 0 ? 1 : -1;
    const at = atTrack(s, side, 12.5);
    const bill = new THREE.Group();
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.3, 4, 0.3), standMat);
    leg.position.y = 2; bill.add(leg);
    const c = billCols[k % billCols.length];
    const panelMat = new THREE.MeshStandardMaterial({ color: c, roughness: 0.5, metalness: 0.1,
      emissive: c, emissiveIntensity: night ? 1.6 : 0.12 });
    disposables.push(panelMat); // per-billboard material -> dispose pri prepnuti trati
    const panel = new THREE.Mesh(new THREE.BoxGeometry(7, 2.4, 0.25), panelMat);
    panel.position.y = 5; panel.castShadow = true; bill.add(panel);
    bill.position.set(at.x, gy(at.x, at.z), at.z);
    bill.rotation.y = at.yaw + (side > 0 ? Math.PI : 0);
    scene.add(bill); objects.push(bill);
  }

  // --- Pneu bariery v par zatackach (vnejsi strana) ---
  const tireMat = new THREE.MeshStandardMaterial({ color: 0x16161a, roughness: 0.95 });
  const tireGeo = new THREE.CylinderGeometry(0.5, 0.5, 0.5, 10);
  const tireSpots = [0.18, 0.42, 0.62, 0.86];
  for (const fr of tireSpots) {
    for (let j = 0; j < 7; j++) {
      const at = atTrack(fr * L + j * 1.2, 1, 9.0);
      for (let st = 0; st < 2; st++) {
        const tm = new THREE.Mesh(tireGeo, tireMat);
        tm.position.set(at.x, gy(at.x, at.z) + 0.25 + st * 0.5, at.z);
        tm.rotation.x = Math.PI / 2; tm.rotation.y = at.yaw;
        scene.add(tm); objects.push(tm);
      }
    }
  }

  // --- Vzdalene kopce (silueta na obzoru) ---
  const hillMat = new THREE.MeshStandardMaterial({ color: biome.hill || 0x4a5a64, roughness: 1.0 });
  const cxh = (minX + maxX) / 2, czh = (minZ + maxZ) / 2;
  const hills = new THREE.Group();
  for (let i = 0; i < 11; i++) {
    const a = (i / 11) * Math.PI * 2;
    const h = 20 + ((i * 7) % 5) * 9;
    const cone = new THREE.Mesh(new THREE.ConeGeometry(45 + (i % 3) * 16, h, 5), hillMat);
    cone.position.set(cxh + Math.cos(a) * 320, h / 2 - 6, czh + Math.sin(a) * 320);
    hills.add(cone);
  }
  scene.add(hills); objects.push(hills);

  return {
    objects,
    dispose() {
      for (const o of objects) { scene.remove(o); o.traverse?.((c) => { c.geometry?.dispose?.(); }); }
      for (const d of disposables) d.dispose?.();
      standMat.dispose(); roofMat.dispose(); crowdMat.dispose(); crowdTex.dispose();
      tireMat.dispose(); tireGeo.dispose(); hillMat.dispose();
    },
  };
}

function makeCrowdTexture() {
  const c = document.createElement('canvas');
  c.width = 128; c.height = 48;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#23262d'; ctx.fillRect(0, 0, c.width, c.height);
  const cols = ['#e84d4d', '#4d8ce8', '#e8d24d', '#ffffff', '#4de88c', '#e87fd0', '#ff9a3a'];
  for (let i = 0; i < 900; i++) {
    ctx.fillStyle = cols[(rndi(c.width * c.height + i) % cols.length)];
    ctx.fillRect((i * 53) % c.width, (i * 31) % c.height, 2, 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
function rndi(n) { let t = (n * 2654435761) >>> 0; t ^= t >>> 13; return t >>> 0; }
